function isPlainObject(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Recursively resolves any node carrying a sibling `i18n` map — e.g.
// { title: 'Applications', i18n: { title: { en: 'Applications', es: 'Solicitudes' } } }
// — into that node's value for `locale`, falling back to the node's own
// (base) value when the locale/key isn't translated. Walks the full
// tree once so callers never need locale-awareness per field. Generic over
// any JSON-shaped tree — used for workpage element definitions and form
// schema/uiSchema alike.
export function resolveI18nTree<T>(node: T, locale: string): T {
  if (Array.isArray(node)) {
    return (node.map(item => resolveI18nTree(item, locale)) as unknown) as T;
  }

  if (!isPlainObject(node)) {
    return node;
  }

  const translations = isPlainObject(node.i18n) ? node.i18n : undefined;
  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(node)) {
    if (key === 'i18n') continue;
    result[key] = resolveI18nTree(value, locale);
  }

  if (translations) {
    for (const [key, byLocale] of Object.entries(translations)) {
      // Reserved shape for a lookup's raw-data-value translation (see
      // LookupFormField) — { sourceLocale, options } describes how to translate
      // VALUES coming back from an API at render time, not a field-name → locale
      // map like every other `i18n` entry. Left on the output untouched instead
      // of being resolved/stripped here, so the component that actually knows
      // how to interpret it still gets it.
      if (key === 'sourceLocale' || key === 'options') continue;
      if (isPlainObject(byLocale) && byLocale[locale] != null) {
        result[key] = byLocale[locale];
      }
    }

    if (typeof translations.sourceLocale === 'string' && isPlainObject(translations.options)) {
      result.i18n = { sourceLocale: translations.sourceLocale, options: translations.options };
    }
  }

  return result as T;
}

// The Form/FormVersion record's own `title` (shown as the page heading and fed into
// the nav breadcrumb — see useTitle) has no typed sibling-i18n slot of its own, same
// situation as a Workpage's `name` (see localizeWorkpageRecord). It rides along inside
// the `UiSchema` blob instead, since that's the one Form field that already round-trips
// arbitrary JSON untouched end to end — including through parseFormDefinition, which
// spreads the raw uiSchema through wholesale but only extracts specific known keys out
// of `options`, silently dropping anything else placed there.
export function resolveFormTitle(
  form: { title?: string; uiSchema?: { i18n?: Record<string, Record<string, any>> } } | null | undefined,
  locale: string
): string {
  return form?.uiSchema?.i18n?.title?.[locale] ?? form?.title ?? '';
}
